import { motion } from "framer-motion";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Link } from "react-router-dom";
import { Award, Users, Truck, Globe, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const WHATSAPP_NUMBER = "218915224966";

// Lynae Product Images
const LYNAE_IMAGES = {
  group1: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/q6goza6r_IMG_6468.jpg",
  group2: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/k6w2pfvj_IMG_6484.jpg",
  vitamin_b12: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/nwegxc9h_IMG_6472.jpg",
  coq10: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/kr9tnjrz_IMG_6475.jpg",
};

const AboutPage = () => {
  const { t, isRTL } = useLanguage();
  
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const milestones = [
    { year: "2012", title: t("companyFounded"), description: t("foundedDescription") },
    { year: "2015", title: t("lynaePartnership"), description: t("lynaePartnershipDesc") },
    { year: "2018", title: t("nationalExpansion"), description: t("expansionDesc") },
    { year: "2024", title: t("digitalTransformation"), description: t("digitalDesc") },
  ];

  const values = [
    { icon: Award, title: t("qualityFirst"), description: t("qualityFirstDesc") },
    { icon: Users, title: t("customerFocus"), description: t("customerFocusDesc") },
    { icon: Truck, title: t("reliableDelivery"), description: t("reliableDeliveryDesc") },
    { icon: Globe, title: t("globalSourcing"), description: t("globalSourcingDesc") },
  ];

  const lynaeFeatures = isRTL 
    ? ["مكونات عالية الجودة", "تصنيع معتمد من FDA", "مجموعة واسعة من المنتجات الصحية", "موثوق به من الملايين حول العالم"]
    : ["Premium quality ingredients", "FDA-compliant manufacturing", "Wide range of health products", "Trusted by millions worldwide"];

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0f172a] overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-[#d4af37] to-[#38bdf8]" />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-[#d4af37] text-[#0f172a]">{t("aboutUs")}</Badge>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              {t("aboutCompany")}
              <span className="block text-[#d4af37] mt-2">بحر الرمال</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {t("aboutHeroDesc")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24" data-testid="story-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                {t("ourStory")}
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("storyText1")}
              </p>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {t("storyText2")}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t("storyText3")}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={LYNAE_IMAGES.group1}
                alt="Lynae Products"
                className="rounded-2xl shadow-2xl"
              />
              <div className={`absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} bg-[#1a365d] text-white p-6 rounded-xl shadow-xl`}>
                <p className="text-3xl font-heading font-bold">12+</p>
                <p className="text-sm">{t("yearsOfService")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-24 bg-[#f8fafc]" data-testid="milestones-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#1a365d]/10 text-[#1a365d]">{isRTL ? "رحلتنا" : "Our Journey"}</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">{t("keyMilestones")}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="text-4xl font-heading font-bold text-[#d4af37] mb-4">
                      {milestone.year}
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{milestone.title}</h3>
                    <p className="text-muted-foreground text-sm">{milestone.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">{t("ourValues")}</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold">
              {isRTL ? "ما نقدمه" : "What We Stand For"}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-2 border-transparent hover:border-[#1a365d]/20 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#1a365d]/10 rounded-full flex items-center justify-center">
                      <value.icon className="w-8 h-8 text-[#1a365d]" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{value.title}</h3>
                    <p className="text-muted-foreground text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lynae Partnership with Real Images */}
      <section className="py-24 bg-[#0f172a]" data-testid="lynae-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">
                {isRTL ? "الموزع الرسمي" : "Official Distributor"}
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                {isRTL ? "فيتامينات ومكملات لينى" : "Lynae Vitamins & Supplements"}
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                {t("lynaeDesc")}
              </p>
              <ul className="space-y-4 mb-8">
                {lynaeFeatures.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/products?brand=Lynae">
                <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90">
                  {t("viewLynaeProducts")}
                  <ArrowIcon className={`${isRTL ? 'mr-2' : 'ml-2'} h-5 w-5`} />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src={LYNAE_IMAGES.group1}
                alt="Lynae Products"
                className="rounded-xl shadow-2xl w-full h-48 object-cover"
              />
              <img
                src={LYNAE_IMAGES.group2}
                alt="Lynae Products"
                className="rounded-xl shadow-2xl w-full h-48 object-cover"
              />
              <img
                src={LYNAE_IMAGES.vitamin_b12}
                alt="Vitamin B-12"
                className="rounded-xl shadow-2xl w-full h-48 object-cover"
              />
              <img
                src={LYNAE_IMAGES.coq10}
                alt="CoQ10"
                className="rounded-xl shadow-2xl w-full h-48 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1a365d]" data-testid="about-cta">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            {isRTL ? "كن شريكنا في ليبيا" : "Partner With Libya's Trusted Distributor"}
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            {isRTL 
              ? "تواصل معنا اليوم لمناقشة كيف يمكننا خدمة احتياجاتك من الأدوية والمستلزمات الطبية."
              : "Contact us today to discuss how we can serve your pharmaceutical and medical supply needs."
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90 h-14 px-8 text-lg">
                {isRTL ? "تواصل عبر واتساب" : "Contact via WhatsApp"}
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a365d] h-14 px-8 text-lg">
                {t("contactUs")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
